<script setup>
/**
 * pages/pos/queue-board.vue — Addendum 16, Modul BX
 * ────────────────────────────────────────────────────
 * Panel "Papan Antrian" — daftar order hari ini yang punya nomor antrian
 * (queue_number, lihat Modul BV), dikelompokkan per queue_status, dengan
 * tombol pindah status manual satu langkah maju (Menunggu -> Diproses ->
 * Siap Diambil -> selesai/hilang dari papan).
 *
 * WAJIB independen dari KDS (§3.2): endpoint GET /pos/queue-board dan POST
 * .../advance murni baca/tulis kolom queue_* di `orders`, tidak menyentuh
 * station_tickets sama sekali — tenant tanpa KDS aktif tetap bisa
 * mengelola papan ini 100% manual.
 *
 * Realtime-ish via polling tiap 5 detik (bukan broadcast/websocket) — sama
 * pola dengan halaman status self-order (lihat CLAUDE.md), dan sengaja TIDAK
 * memakai channel `tenant.{tenant}.queue-board`/event `QueueStatusUpdated`
 * yang itu punya Modul BY (Customer Display), di luar cakupan modul ini.
 * Polling ini juga yang membuat perubahan OTOMATIS dari Modul BW (KDS)
 * ikut ter-refresh di panel ini tanpa kasir klik apa pun (§3.3) — tombol
 * "geser manual" di bawah tetap selalu aktif untuk override kapan saja,
 * terlepas dari apakah order itu terhubung KDS atau tidak.
 */
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'pos',
    action: 'read',
    subject: 'Sales',
  },
})

const router = useRouter()
const { request } = useApiRequest()

const orders = ref([])
const initialLoading = ref(true)
const advancingId = ref(null)

const STATUS_GROUPS = [
  { status: 'waiting', title: 'Menunggu', icon: 'ri-time-line', color: 'warning', nextLabel: 'Tandai Diproses' },
  { status: 'preparing', title: 'Diproses', icon: 'ri-fire-line', color: 'info', nextLabel: 'Tandai Siap Diambil' },
  { status: 'ready', title: 'Siap Diambil', icon: 'ri-check-double-line', color: 'success', nextLabel: 'Tandai Sudah Diambil' },
]

const ORDER_TYPE_LABELS = {
  dine_in: 'Dine-in',
  takeaway: 'Takeaway',
  delivery: 'Delivery',
}

const groupedOrders = computed(() => {
  const groups = {}
  for (const group of STATUS_GROUPS) groups[group.status] = []
  for (const order of orders.value) {
    if (groups[order.queue_status]) groups[order.queue_status].push(order)
  }
  
  return groups
})

const fetchBoard = async ({ silent = false } = {}) => {
  if (!silent) initialLoading.value = true
  try {
    const res = await useApi('/api/v1/pos/queue-board').get()

    orders.value = res?.data ?? res ?? []
  } catch (err) {
    // Polling latar belakang — gagal sesekali (network hiccup) tidak perlu
    // toast tiap 5 detik, cukup log supaya tidak jadi unhandled rejection.
    console.warn('[Papan Antrian] Gagal memuat data:', err)
  } finally {
    initialLoading.value = false
  }
}

const advance = async order => {
  advancingId.value = order.id

  const res = await request(() =>
    useApi(`/api/v1/pos/queue-board/${order.id}/advance`).post(),
  )

  advancingId.value = null
  if (res) {
    await fetchBoard({ silent: true })
  }
}

let pollTimer = null

onMounted(() => {
  fetchBoard()
  pollTimer = setInterval(() => fetchBoard({ silent: true }), 5000)
})

onBeforeUnmount(() => {
  clearInterval(pollTimer)
})

const backToPos = () => router.push('/pos')
</script>

<template>
  <div class="queue-board pa-4">
    <div class="d-flex align-center justify-space-between mb-4">
      <div>
        <h4 class="text-h4 font-weight-medium mb-1">
          Papan Antrian
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Order hari ini dengan nomor antrian — geser status secara manual.
        </p>
      </div>
      <VBtn
        variant="tonal"
        prepend-icon="ri-arrow-left-line"
        @click="backToPos"
      >
        Kembali ke Kasir
      </VBtn>
    </div>

    <div
      v-if="initialLoading"
      class="d-flex justify-center pa-8"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </div>

    <VRow v-else>
      <VCol
        v-for="group in STATUS_GROUPS"
        :key="group.status"
        cols="12"
        md="4"
      >
        <VCard>
          <VCardTitle class="d-flex align-center gap-2">
            <VIcon
              :icon="group.icon"
              :color="group.color"
            />
            {{ group.title }}
            <VChip
              size="small"
              variant="tonal"
              :color="group.color"
            >
              {{ groupedOrders[group.status].length }}
            </VChip>
          </VCardTitle>

          <VDivider />

          <VCardText class="queue-column">
            <p
              v-if="groupedOrders[group.status].length === 0"
              class="text-body-2 text-medium-emphasis text-center pa-4 mb-0"
            >
              Tidak ada order.
            </p>

            <VCard
              v-for="order in groupedOrders[group.status]"
              :key="order.id"
              variant="tonal"
              :color="group.color"
              class="mb-3 pa-3"
            >
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="queue-number-badge">
                  {{ order.queue_number }}
                </div>
                <VChip
                  size="small"
                  variant="outlined"
                >
                  {{ ORDER_TYPE_LABELS[order.order_type] ?? order.order_type }}
                </VChip>
              </div>

              <div class="text-body-2 mb-1 d-flex align-center gap-2">
                <strong>{{ order.order_number }}</strong>
                <VChip
                  v-if="order.source === 'self_order'"
                  size="x-small"
                  variant="tonal"
                  color="info"
                  prepend-icon="ri-qr-scan-2-line"
                >
                  Self-Order
                </VChip>
              </div>
              <div
                v-if="order.table_code"
                class="text-caption text-medium-emphasis mb-1 d-flex align-center gap-1"
              >
                <VIcon icon="ri-table-2" size="14" />
                Meja {{ order.table_code }}
              </div>
              <div
                v-if="order.customer_name"
                class="text-caption text-medium-emphasis mb-3"
              >
                {{ order.customer_name }}
              </div>

              <VBtn
                block
                size="small"
                color="primary"
                :loading="advancingId === order.id"
                @click="advance(order)"
              >
                {{ group.nextLabel }}
              </VBtn>
            </VCard>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.queue-column {
  min-height: 200px;
  max-height: 65vh;
  overflow-y: auto;
}
.queue-number-badge {
  font-size: 1.75rem;
  font-weight: bold;
  line-height: 1;
}
</style>
